import { StyleSheet, css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useState } from 'react';
import { Button } from '../../../../web/components/Button';
import { DayPicker } from '../../../../web/components/DayPicker';
import { Modal } from '../../../../web/components/Modal';
import { borderColor } from '../../../../web/styles/colors';
import { TimePicker } from '../../../components/TimePicker';
import { Fill } from '../../../components/leadView/components/Fill';
import { Group } from '../../../components/leadView/components/Group';

interface IProps {
	initialValue?: Date;
	isOpen: boolean;
	onCancel: () => void;
	onSave: (endDate: Date) => void;
}

const styles = StyleSheet.create({
	label: {
		fontWeight: 600,
		marginBottom: 8,
	},
	modalBody: {
		padding: 20,
	},
	modalContent: {
		minWidth: 400,
	},
	modalFooter: {
		borderTop: `1px solid ${borderColor}`,
		padding: 20,
	},
	modalHeader: {
		borderBottom: `1px solid ${borderColor}`,
		padding: 20,
	},
});

export const CustomDurationPicker: React.FC<IProps> = ({ initialValue, isOpen, onCancel, onSave }) => {
	const [selectedDate, setSelectedDate] = useState<Date>(initialValue || moment().add(1, 'day').toDate());
	const [selectedTime, setSelectedTime] = useState<moment.Moment>(
		moment(initialValue || moment().add(1, 'day').toDate())
	);

	const handleDateChange = (date: Date) => {
		setSelectedDate(date);
		// Update time to maintain the selected time
		const updatedTime = moment(date).set({
			hour: selectedTime.hour(),
			minute: selectedTime.minute(),
		});
		setSelectedTime(updatedTime);
	};

	const handleTimeChange = (time: moment.Moment) => {
		setSelectedTime(time);
		// Update selected date with new time
		const updatedDate = moment(selectedDate).set({
			hour: time.hour(),
			minute: time.minute(),
		});
		setSelectedDate(updatedDate.toDate());
	};

	const handleSave = () => {
		const finalDate = moment(selectedDate).set({
			hour: selectedTime.hour(),
			minute: selectedTime.minute(),
			second: 0,
			millisecond: 0,
		});

		// Validate that the selected date/time is in the future
		if (finalDate.isSameOrBefore(moment())) {
			// TODO: Show error message to user (could use toast/alert)
			console.warn('Selected end date must be in the future');
			return;
		}

		onSave(finalDate.toDate());
	};

	const handleCancel = () => {
		onCancel();
	};

	return (
		<Modal isOpen={isOpen} onRequestClose={handleCancel}>
			<div className={css(styles.modalContent)}>
				<div className={css(styles.modalHeader)}>
					<h2>Select End Date and Time</h2>
				</div>
				<div className={css(styles.modalBody)}>
					<Group vertical={true} spacingSize={20}>
						<div>
							<div className={css(styles.label)}>Date:</div>
							<DayPicker
								canChangeToNextMonth={true}
								canChangeToPreviousMonth={true}
								onDayClick={handleDateChange}
								selectedDays={selectedDate}
								showOverflowDates={false}
								disabledDays={[{ before: new Date() }]}
							/>
						</div>
						<div>
							<div className={css(styles.label)}>Time:</div>
							<TimePicker defaultTime={selectedTime} increment={15} onChange={handleTimeChange} />
						</div>
					</Group>
				</div>
				<div className={css(styles.modalFooter)}>
					<Group spacingSize={10}>
						<Fill />
						<Button kind='secondary' label='Cancel' onClick={handleCancel} />
						<Button kind='primary' label='Save' onClick={handleSave} />
					</Group>
				</div>
			</div>
		</Modal>
	);
};
